import { getAllRecords } from '../db/indexeddb';
import { RECORD_TYPES, STATUSES, STATUS_LABELS, TYPE_LABELS, type GvmRecord } from '../lib/types';
import { recordRowHtml } from './shared';

export async function renderVisionMap(container: HTMLElement, query: URLSearchParams): Promise<void> {
  const records = await getAllRecords(false);

  const categories = uniqueSorted(records.map((r) => r.category).filter(Boolean));
  const owners = uniqueSorted(records.map((r) => r.owner).filter(Boolean));
  const lanes = uniqueSorted(records.map((r) => r.execution_lane).filter(Boolean));
  const projects = records.filter((r) => ['project', 'active_priority'].includes(r.type));

  container.innerHTML = `
    <h2 style="font-size:19px; margin-bottom:10px;">Master Vision Map</h2>
    <input id="vm-search" placeholder="Search title, description, notes…" value="${escapeAttr(query.get('q') ?? '')}" style="margin-bottom:10px;" />
    <div class="filter-bar">
      ${select('vm-type', 'Type', [['', 'All types'], ...RECORD_TYPES.map((t) => [t, TYPE_LABELS[t]] as [string, string])], query.get('type'))}
      ${select('vm-status', 'Status', [['', 'All statuses'], ...STATUSES.map((s) => [s, STATUS_LABELS[s]] as [string, string])], query.get('status'))}
      ${select('vm-category', 'Category', [['', 'All categories'], ...categories.map((c) => [c, c] as [string, string])], query.get('category'))}
      ${select('vm-owner', 'Owner', [['', 'All owners'], ...owners.map((o) => [o, o] as [string, string])], query.get('owner'))}
      ${select('vm-lane', 'Lane', [['', 'All lanes'], ...lanes.map((l) => [l, l] as [string, string])], query.get('lane'))}
      ${select('vm-parent', 'Parent', [['', 'All projects'], ...projects.map((p) => [p.id, p.title] as [string, string])], query.get('parent'))}
      ${select('vm-sort', 'Sort', [['overall_score', 'By score'], ['priority_rank', 'By rank'], ['updated_at', 'Recently updated']], query.get('sort'))}
    </div>
    <div id="vm-count" class="field-hint" style="margin-bottom:8px;"></div>
    <div id="vm-list"></div>
  `;

  const searchEl = container.querySelector('#vm-search') as HTMLInputElement;
  const typeEl = container.querySelector('#vm-type') as HTMLSelectElement;
  const statusEl = container.querySelector('#vm-status') as HTMLSelectElement;
  const categoryEl = container.querySelector('#vm-category') as HTMLSelectElement;
  const ownerEl = container.querySelector('#vm-owner') as HTMLSelectElement;
  const laneEl = container.querySelector('#vm-lane') as HTMLSelectElement;
  const parentEl = container.querySelector('#vm-parent') as HTMLSelectElement;
  const sortEl = container.querySelector('#vm-sort') as HTMLSelectElement;
  const listEl = container.querySelector('#vm-list') as HTMLElement;
  const countEl = container.querySelector('#vm-count') as HTMLElement;

  function apply() {
    let filtered = records.filter((r) => {
      if (typeEl.value && r.type !== typeEl.value) return false;
      if (statusEl.value && r.status !== statusEl.value) return false;
      if (categoryEl.value && r.category !== categoryEl.value) return false;
      if (ownerEl.value && r.owner !== ownerEl.value) return false;
      if (laneEl.value && r.execution_lane !== laneEl.value) return false;
      if (parentEl.value && r.parent_id !== parentEl.value) return false;
      const q = searchEl.value.trim().toLowerCase();
      if (q) {
        const hay = `${r.title} ${r.description} ${r.notes}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    filtered = sortRecords(filtered, sortEl.value);
    countEl.textContent = `${filtered.length} record${filtered.length === 1 ? '' : 's'}`;
    listEl.innerHTML = filtered.length
      ? filtered.map(recordRowHtml).join('')
      : '<div class="empty">Nothing matches these filters.</div>';
  }

  [searchEl, typeEl, statusEl, categoryEl, ownerEl, laneEl, parentEl, sortEl].forEach((elm) => {
    elm.addEventListener('input', apply);
    elm.addEventListener('change', apply);
  });

  apply();
}

function sortRecords(records: GvmRecord[], sort: string): GvmRecord[] {
  const copy = [...records];
  if (sort === 'priority_rank') {
    copy.sort((a, b) => {
      if (a.priority_rank == null && b.priority_rank == null) return 0;
      if (a.priority_rank == null) return 1;
      if (b.priority_rank == null) return -1;
      return a.priority_rank - b.priority_rank;
    });
  } else if (sort === 'updated_at') {
    copy.sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  } else {
    copy.sort((a, b) => b.overall_score - a.overall_score);
  }
  return copy;
}

function uniqueSorted(values: string[]): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b));
}

function select(id: string, _label: string, options: [string, string][], selected: string | null): string {
  return `<select id="${id}">${options.map(([v, l]) =>
    `<option value="${escapeAttr(v)}" ${v === (selected ?? '') ? 'selected' : ''}>${l}</option>`,
  ).join('')}</select>`;
}

function escapeAttr(s: string): string {
  return s.replace(/"/g, '&quot;');
}
